import React, { useState } from 'react';
import styled from 'styled-components';
import { useRegistration } from '../../../contexts/RegistrationContext';

const StepContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h2`
  color: #000;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const Description = styled.p`
  color: #666;
  margin-bottom: 2rem;
`;

const InfoNote = styled.div`
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 15px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #155724;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const AccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Accordion = styled.div`
  border: 1px solid #ddd;
  border-radius: 15px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const AccordionHeader = styled.button`
  width: 100%;
  padding: 1.5rem;
  background: #f8f9fa;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: #000;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
  }
  
  &:focus {
    outline: none;
    background: #e9ecef;
  }
`;

const AccordionIcon = styled.span<{ isOpen: boolean }>`
  font-size: 1.2rem;
  transition: transform 0.2s;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const AccordionContent = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-out;
`;

const AccordionBody = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #ddd;
`;

const ServiceList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ServiceItem = styled.li`
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ServiceIcon = styled.span`
  color: #28a745;
  font-weight: bold;
  margin-top: 0.1rem;
  flex-shrink: 0;
`;

const ServiceText = styled.div`
  flex: 1;
  line-height: 1.5;
  color: #000;
`;

const ServiceSubList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0 0;
`;

const ServiceSubItem = styled.li`
  padding: 0.25rem 0;
  color: #666;
  font-size: 0.95rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const ComingSoon = styled.span`
  background: #ffc107;
  color: #856404;
  padding: 0.2rem 0.5rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-left: 0.5rem;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 15px;
  background: #f8f9fa;
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 10px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e9ecef;
  }
`;

const OptionContent = styled.div`
  flex: 1;
`;

const OptionTitle = styled.div`
  font-weight: 500;
  color: #000;
  margin-bottom: 0.25rem;
`;

const OptionDescription = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #000;
  font-size: 0.9rem;
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 25px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 25px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &.primary {
    background: #000;
    color: white;
    
    &:hover:not(:disabled) {
      background: #333;
      transform: translateY(-1px);
    }
  }
  
  &.secondary {
    background: #6c757d;
    color: white;
    
    &:hover:not(:disabled) {
      background: #5a6268;
      transform: translateY(-1px);
    }
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const AdditionalServices: React.FC = () => {
  const { registrationData, setCurrentStep, saveAdditionalServices } = useRegistration();
  
  const [comments, setComments] = useState(registrationData.additional_comments || '');
  const [communication, setCommunication] = useState(registrationData.preferred_communication || '');
  const [isLoading, setIsLoading] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);
  
  // Parse additional services from charter preferences
  const parsedCharterPreferences = registrationData.charter_preferences 
    ? JSON.parse(registrationData.charter_preferences)
    : {};
  
  const [additionalServices, setAdditionalServices] = useState<Record<string, boolean>>({
    document_translation: parsedCharterPreferences.document_translation || false,
    bank_account_ra: parsedCharterPreferences.bank_account_ra || false,
    bank_account_eu: parsedCharterPreferences.bank_account_eu || false,
    premium_package: parsedCharterPreferences.premium_package || false,
  });

  const handleAccordionToggle = (index: number) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  const handleAdditionalServiceChange = (key: string, checked: boolean) => {
    setAdditionalServices(prev => ({ ...prev, [key]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await saveAdditionalServices({
        additional_services: JSON.stringify(additionalServices),
        additional_comments: comments,
        preferred_communication: communication,
      });
      setCurrentStep(6);
    } catch (error) {
      console.error('Failed to save additional services:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(4);
  };

  return (
    <StepContainer>
      <Title>Step 5: Services Overview</Title>
      <Description>
        Review the services included in your company registration package and ongoing service contract.
      </Description>
      
      <InfoNote>
        <strong>Note:</strong> All services listed below are included in your registration package. 
        You can request additional services or modifications through the comments section.
      </InfoNote>
      
      <Form onSubmit={handleSubmit}>
        <AccordionContainer>
          <Accordion>
            <AccordionHeader 
              type="button" 
              onClick={() => handleAccordionToggle(0)}
              aria-expanded={openAccordion === 0}
            >
              Services included in registration
              <AccordionIcon isOpen={openAccordion === 0}>▼</AccordionIcon>
            </AccordionHeader>
            <AccordionContent isOpen={openAccordion === 0}>
              <AccordionBody>
                <ServiceList>
                  <ServiceItem>
                    <ServiceIcon>✓</ServiceIcon>
                    <ServiceText>
                      Preparation of standard documents for company registration
                    </ServiceText>
                  </ServiceItem>
                  <ServiceItem>
                    <ServiceIcon>✓</ServiceIcon>
                    <ServiceText>
                      Submission of the document package for registration in the Commercial Register of the Republic of Armenia
                    </ServiceText>
                  </ServiceItem>
                  <ServiceItem>
                    <ServiceIcon>✓</ServiceIcon>
                    <ServiceText>
                      Support throughout the registration process in the Armenian Commercial Register
                    </ServiceText>
                  </ServiceItem>
                  <ServiceItem>
                    <ServiceIcon>✓</ServiceIcon>
                    <ServiceText>
                      Support in opening a bank account in AMD in an Armenian bank, proposed after analysis of the founders' documents
                    </ServiceText>
                  </ServiceItem>
                </ServiceList>
              </AccordionBody>
            </AccordionContent>
          </Accordion>

          <Accordion>
            <AccordionHeader 
              type="button" 
              onClick={() => handleAccordionToggle(1)}
              aria-expanded={openAccordion === 1}
            >
              Services included in the service contract
              <AccordionIcon isOpen={openAccordion === 1}>▼</AccordionIcon>
            </AccordionHeader>
            <AccordionContent isOpen={openAccordion === 1}>
              <AccordionBody>
                <ServiceList>
                  <ServiceItem>
                    <ServiceIcon>✓</ServiceIcon>
                    <ServiceText>
                      Registered address with secretarial support
                    </ServiceText>
                  </ServiceItem>
                  <ServiceItem>
                    <ServiceIcon>✓</ServiceIcon>
                    <ServiceText>
                      Organization and maintenance of accounting, processing of primary documents including:
                      <ServiceSubList>
                        <ServiceSubItem>
                          <ServiceIcon>•</ServiceIcon>
                          Processing up to 10 primary documents per month
                        </ServiceSubItem>
                        <ServiceSubItem>
                          <ServiceIcon>•</ServiceIcon>
                          Processing 2 bank statements
                        </ServiceSubItem>
                        <ServiceSubItem>
                          <ServiceIcon>•</ServiceIcon>
                          Payroll calculation for 1 employee
                        </ServiceSubItem>
                        <ServiceSubItem>
                          <ServiceIcon>•</ServiceIcon>
                          Submission of regular reports (monthly, quarterly, annual) as required by the legislation of the Republic of Armenia
                        </ServiceSubItem>
                      </ServiceSubList>
                    </ServiceText>
                  </ServiceItem>
                  <ServiceItem>
                    <ServiceIcon>✓</ServiceIcon>
                    <ServiceText>
                      Access to the Keeps.pro online service with full functionality for independent preparation of primary documents
                    </ServiceText>
                  </ServiceItem>
                  <ServiceItem>
                    <ServiceIcon>✓</ServiceIcon>
                    <ServiceText>
                      A .am domain for email and website
                      <ComingSoon>*Coming soon</ComingSoon>
                    </ServiceText>
                  </ServiceItem>
                  <ServiceItem>
                    <ServiceIcon>✓</ServiceIcon>
                    <ServiceText>
                      A phone number with +374 (Armenia) code with voicemail function
                      <ComingSoon>*Coming soon</ComingSoon>
                    </ServiceText>
                  </ServiceItem>
                </ServiceList>
              </AccordionBody>
            </AccordionContent>
          </Accordion>

          <Accordion>
            <AccordionHeader 
              type="button" 
              onClick={() => handleAccordionToggle(2)}
              aria-expanded={openAccordion === 2}
            >
              Additional Services
              <AccordionIcon isOpen={openAccordion === 2}>▼</AccordionIcon>
            </AccordionHeader>
            <AccordionContent isOpen={openAccordion === 2}>
              <AccordionBody>
                <CheckboxGroup>
                  <CheckboxOption>
                    <input
                      type="checkbox"
                      checked={additionalServices.document_translation || false}
                      onChange={(e) => handleAdditionalServiceChange('document_translation', e.target.checked)}
                    />
                    <OptionContent>
                      <OptionTitle>Document Translation & Apostille</OptionTitle>
                      <OptionDescription>
                        Obtaining a set of documents with a licensed translation into the requested language and an apostille (specify the language in the comments)
                      </OptionDescription>
                    </OptionContent>
                  </CheckboxOption>

                  <CheckboxOption>
                    <input
                      type="checkbox"
                      checked={additionalServices.bank_account_ra || false}
                      onChange={(e) => handleAdditionalServiceChange('bank_account_ra', e.target.checked)}
                    />
                    <OptionContent>
                      <OptionTitle>Bank Account in RA</OptionTitle>
                      <OptionDescription>
                        Assistance with opening a bank account in EUR/USD/GBP in a RA bank*
                      </OptionDescription>
                    </OptionContent>
                  </CheckboxOption>

                  <CheckboxOption>
                    <input
                      type="checkbox"
                      checked={additionalServices.bank_account_eu || false}
                      onChange={(e) => handleAdditionalServiceChange('bank_account_eu', e.target.checked)}
                    />
                    <OptionContent>
                      <OptionTitle>Bank Account in EU</OptionTitle>
                      <OptionDescription>
                        Assistance with opening an account in USD/EUR in a licensed EU financial institution*
                      </OptionDescription>
                    </OptionContent>
                  </CheckboxOption>

                  <CheckboxOption>
                    <input
                      type="checkbox"
                      checked={additionalServices.premium_package || false}
                      onChange={(e) => handleAdditionalServiceChange('premium_package', e.target.checked)}
                    />
                    <OptionContent>
                      <OptionTitle>Premium Package</OptionTitle>
                      <OptionDescription>
                        Package: "+3 employees, +2 bank accounts, +30 primary documents"
                      </OptionDescription>
                    </OptionContent>
                  </CheckboxOption>
                </CheckboxGroup>
                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  *Depends on the type of activity and the residency of the beneficial owners.
                </div>
              </AccordionBody>
            </AccordionContent>
          </Accordion>
        </AccordionContainer>

        <FormGroup>
          <Label>Additional Comments or Questions</Label>
          <TextArea
            placeholder="Please describe any specific requirements, questions, or additional services you need..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Preferred Method of Communication (Required) *</Label>
          <Select
            value={communication}
            onChange={(e) => setCommunication(e.target.value)}
            required
          >
            <option value="">Select communication method...</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="telegram">Telegram</option>
            <option value="viber">Viber</option>
          </Select>
        </FormGroup>

        <ButtonGroup>
          <Button type="button" className="secondary" onClick={handleBack}>
            Back: Charter Details
          </Button>
          <Button type="submit" className="primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Next: Review & Submit'}
          </Button>
        </ButtonGroup>
      </Form>
    </StepContainer>
  );
};

export default AdditionalServices;